import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, { Background, Controls, ReactFlowProvider, useReactFlow } from 'reactflow';
import 'reactflow/dist/style.css';
import './policyGraph.css';
import { FoldVertical, Search, UnfoldVertical } from 'lucide-react';
import { logDataModels } from '../../dataModelsLogger';
import { DialogShell, ghostBtn, iconBtn } from '../../components/dataModelsUi';
import { useGovernance, type GovernanceLevel } from '../../components/governanceStore';
import { buildGraphCatalog } from './policyGraphFromLive';
import PolicyGraphNode, { type PolicyGraphNodeData } from './PolicyGraphNode';
import { buildGraphView, collectExpandKeys, expandKey, expandKeysForNode, nodeKindFromId, primaryExpandKey } from './policyGraphLayout';
import {
  assetById,
  effectiveForAsset,
  effectiveForColumn,
  featuredThenRest,
  hitsForAsset,
  identifyOverlaps,
  inheritedModels,
  matchedAssets,
  matchingColumns,
  modelById,
  ownerName,
  overlapsForPolicy,
  overlapsForResource,
  policyById,
  policyCoverage,
  resolveGraphContext,
  scopedAssets,
  vaultById,
} from './policyGraphResolve';
import {
  OWNERSHIP_LABEL,
  POLICY_TYPE_LABEL,
  type GraphCatalog,
  type GraphContext,
  type GraphPolicyStatus,
  type GraphPolicyType,
  type OwnershipLevel,
  type PolicyOverlap,
} from './policyGraphTypes';

const NODE_TYPES = { policyGraph: PolicyGraphNode };

const SELECT = 'h-8 rounded-md border border-[#D8DCE2] bg-white px-2 text-[12px] text-[#1C1C1C]';
const LABEL = 'text-[10px] font-medium uppercase tracking-wide text-neutral-500';

export default function PolicyGraphDialog({
  level,
  spaceId,
  modelId,
  tableId,
  onClose,
}: {
  level: GovernanceLevel;
  spaceId: string;
  modelId?: string;
  tableId?: string;
  onClose: () => void;
}) {
  const { policies, assets, policyModels, vaults } = useGovernance();
  const catalog = useMemo(
    () => buildGraphCatalog({ vaults, models: policyModels, assets, policies }),
    [assets, policies, policyModels, vaults],
  );
  const overlaps = useMemo(() => identifyOverlaps(catalog), [catalog]);
  const initial = useMemo(
    () => resolveGraphContext(catalog, { level, spaceId, modelId, tableId }),
    [catalog, level, modelId, spaceId, tableId],
  );

  return (
    <DialogShell
      title="Policy Graph"
      subtitle="Tenant WorkSpaces, catalogs, assets, and persisted policies. Effective restrictions are a frontend simulation, not live enforcement."
      isLight
      size="page"
      onClose={onClose}
      footer={<button type="button" className={ghostBtn(true)} onClick={onClose}>Close</button>}
    >
      {!catalog.vaults.length ? (
        <p className="px-4 py-8 text-sm text-neutral-500">No WorkSpace data is available for the policy graph.</p>
      ) : (
        <ReactFlowProvider>
          <PolicyGraphBody catalog={catalog} overlaps={overlaps} initial={initial} />
        </ReactFlowProvider>
      )}
    </DialogShell>
  );
}

const EMPTY_FILTERS = { query: '', type: 'all' as const, ownership: 'all' as const, status: 'all' as const };

function PolicyGraphBody({
  catalog,
  overlaps,
  initial,
}: {
  catalog: GraphCatalog;
  overlaps: PolicyOverlap[];
  initial: GraphContext;
}) {
  const { fitView } = useReactFlow();
  const [context, setContext] = useState<GraphContext>(initial);
  const [expanded, setExpanded] = useState<Set<string>>(() => collectExpandKeys(catalog, initial, EMPTY_FILTERS));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedPolicyId, setSelectedPolicyId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [type, setType] = useState<'all' | GraphPolicyType>('all');
  const [ownership, setOwnership] = useState<'all' | OwnershipLevel>('all');
  const [status, setStatus] = useState<'all' | GraphPolicyStatus>('all');
  const [userId, setUserId] = useState(catalog.users[0]?.id ?? '');
  const [openAssetId, setOpenAssetId] = useState<string | null>(null);

  const user = catalog.users.find((item) => item.id === userId) ?? catalog.users[0];
  const vault = vaultById(catalog, context.vaultId);

  useEffect(() => {
    logDataModels('app', 'policy_graph_open', {
      level: context.level,
      vaultId: context.vaultId,
      modelId: context.modelId,
      assetId: context.assetId,
    });
  }, [context.assetId, context.level, context.modelId, context.vaultId]);

  const { nodes: rawNodes, edges } = useMemo(
    () => buildGraphView(catalog, {
      context,
      expanded,
      selectedPolicyId,
      filters: { query, type, ownership, status },
    }),
    [catalog, context, expanded, ownership, query, selectedPolicyId, status, type],
  );

  const toggleExpand = useCallback((nodeId: string) => {
    const parsed = nodeKindFromId(nodeId);
    if (parsed.kind !== 'policy' && parsed.kind !== 'model' && parsed.kind !== 'asset') return;
    const policy = parsed.kind === 'policy' ? policyById(catalog, parsed.rest) : undefined;
    const keys = expandKeysForNode(nodeId);
    const open = primaryExpandKey(parsed.kind, parsed.rest, policy);
    if (!open) return;
    setExpanded((current) => {
      const next = new Set(current);
      const isOpen = keys.some((key) => next.has(key));
      if (isOpen) keys.forEach((key) => next.delete(key));
      else {
        next.add(open);
        if (policy?.dependsOnAssetId) next.add(expandKey('policy', policy.id, 'depends'));
      }
      logDataModels('app', 'policy_graph_expand', { nodeId, key: open, expanded: !isOpen });
      return next;
    });
  }, [catalog]);

  const nodes = useMemo(
    () => rawNodes.map((node) => ({
      ...node,
      selected: node.id === selectedId,
      data: {
        ...node.data,
        onToggleExpand: () => toggleExpand(node.id),
      } satisfies PolicyGraphNodeData,
    })),
    [rawNodes, selectedId, toggleExpand],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => fitView({ padding: 0.16 }), 50);
    return () => window.clearTimeout(timer);
  }, [expanded, fitView, nodes.length]);

  useEffect(() => {
    if (!selectedId) return;
    const parsed = nodeKindFromId(selectedId);
    if (parsed.kind !== 'column') return;
    const [assetId, columnName] = [parsed.rest.split(':')[0], parsed.rest.split(':').slice(1).join(':')];
    const asset = assetById(catalog, assetId);
    const column = asset?.columns.find((item) => item.name === columnName);
    if (!asset || !column) return;
    const effective = effectiveForColumn(catalog, asset, column, user);
    if (effective.kind === 'unresolved') {
      logDataModels('warning', 'policy_graph_unresolved_conflict', {
        asset: asset.name,
        column: column.name,
        kind: effective.kind,
      });
    }
  }, [catalog, selectedId, user]);

  const filters = { query, type, ownership, status };
  const expandAll = () => {
    const keys = collectExpandKeys(catalog, context, filters, 'all');
    setExpanded(keys);
    logDataModels('app', 'policy_graph_expand_all', { count: keys.size, vaultId: context.vaultId });
  };
  const collapseRoot = () => {
    setExpanded(new Set());
    logDataModels('app', 'policy_graph_collapse_root', { vaultId: context.vaultId });
  };
  const reset = () => {
    setExpanded(collectExpandKeys(catalog, context, EMPTY_FILTERS));
    setSelectedId(null);
    setSelectedPolicyId(null);
    setQuery('');
    setType('all');
    setOwnership('all');
    setStatus('all');
    setOpenAssetId(null);
    window.requestAnimationFrame(() => fitView({ padding: 0.16 }));
  };

  const selectNode = (id: string) => {
    const parsed = nodeKindFromId(id);
    if (parsed.kind === 'overflow') {
      const parts = parsed.rest.split(':');
      if (parts[0] === 'policy') {
        setSelectedPolicyId(parts[1]);
        setSelectedId(`policy:${parts[1]}`);
        return;
      }
      if (parts[0] === 'model') {
        setSelectedId(`model:${parts[1]}`);
        return;
      }
      if (parts[0] === 'asset') {
        setSelectedId(`asset:${parts[1]}`);
        return;
      }
    }
    setSelectedId(id);
    if (parsed.kind === 'policy') setSelectedPolicyId(parsed.rest);
  };

  const switchVault = (vaultId: string) => {
    const next = { level: 'vault' as const, vaultId };
    setContext(next);
    setExpanded(collectExpandKeys(catalog, next, filters));
    setSelectedId(null);
    setSelectedPolicyId(null);
    setOpenAssetId(null);
  };

  return (
    <div className="policy-graph flex h-full min-h-0 flex-col bg-white text-neutral-900">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-neutral-200 px-3 py-2">
        <div className="relative min-w-[180px] flex-1">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search policies and assets"
            className="h-8 w-full rounded-md border border-[#D8DCE2] bg-white py-0 pl-8 pr-2 text-[12px] text-[#1C1C1C] placeholder:text-neutral-400"
          />
        </div>
        <select className={SELECT} value={context.vaultId} onChange={(event) => switchVault(event.target.value)} aria-label="DataVault">
          {catalog.vaults.map((item) => (
            <option key={item.id} value={item.id}>{item.name}</option>
          ))}
        </select>
        <select className={SELECT} value={type} onChange={(event) => setType(event.target.value as 'all' | GraphPolicyType)} aria-label="Policy type">
          <option value="all">All types</option>
          {Object.entries(POLICY_TYPE_LABEL).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
        <select className={SELECT} value={ownership} onChange={(event) => setOwnership(event.target.value as 'all' | OwnershipLevel)} aria-label="Ownership">
          <option value="all">All owners</option>
          {Object.entries(OWNERSHIP_LABEL).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
        <select className={SELECT} value={status} onChange={(event) => setStatus(event.target.value as 'all' | GraphPolicyStatus)} aria-label="Status">
          <option value="all">All statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="DRAFT">Draft</option>
        </select>
        <select className={SELECT} value={userId} onChange={(event) => setUserId(event.target.value)} aria-label="User context">
          {catalog.users.map((item) => (
            <option key={item.id} value={item.id}>{item.label}</option>
          ))}
        </select>
        <button type="button" className={iconBtn(true)} title="Expand all" aria-label="Expand all" onClick={expandAll}>
          <UnfoldVertical size={14} />
        </button>
        <button type="button" className={iconBtn(true)} title="Collapse to root" aria-label="Collapse to root" onClick={collapseRoot}>
          <FoldVertical size={14} />
        </button>
        <button type="button" className={ghostBtn(true)} onClick={reset}>Reset</button>
        <p className="w-full text-[11px] text-neutral-500">
          {vault?.name ?? 'DataVault'} · dashed card = inherited policy · solid owns / protects · dashed line = inherited / overlaps · dotted = depends
        </p>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="h-full min-h-[420px] bg-white">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            onNodeClick={(_, node) => selectNode(node.id)}
            defaultEdgeOptions={{ type: 'smoothstep' }}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            panOnScroll
            minZoom={0.35}
            maxZoom={1.5}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable
            proOptions={{ hideAttribution: true }}
          >
            <Background color="#E6E8EB" gap={18} size={1} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>
        <aside className="min-h-0 overflow-y-auto border-t border-neutral-200 p-3 lg:border-l lg:border-t-0">
          <GraphDetails
            catalog={catalog}
            overlaps={overlaps}
            selectedId={selectedId}
            selectedPolicyId={selectedPolicyId}
            userId={user?.id ?? ''}
            openAssetId={openAssetId}
            onOpenAsset={setOpenAssetId}
            onSelect={selectNode}
            onExpandAsset={(assetId) => {
              setExpanded((current) => new Set(current).add(expandKey('asset', assetId, 'columns')));
              setSelectedId(`asset:${assetId}`);
            }}
          />
        </aside>
      </div>
    </div>
  );
}

function GraphDetails({
  catalog,
  overlaps,
  selectedId,
  selectedPolicyId,
  userId,
  openAssetId,
  onOpenAsset,
  onSelect,
  onExpandAsset,
}: {
  catalog: GraphCatalog;
  overlaps: PolicyOverlap[];
  selectedId: string | null;
  selectedPolicyId: string | null;
  userId: string;
  openAssetId: string | null;
  onOpenAsset: (id: string | null) => void;
  onSelect: (id: string) => void;
  onExpandAsset: (assetId: string) => void;
}) {
  const user = catalog.users.find((item) => item.id === userId) ?? catalog.users[0];
  if (!user) return <EmptyDetails />;
  if (!selectedId) {
    return (
      <div>
        <p className="text-[13px] font-medium text-neutral-900">Select a node</p>
        <p className="mt-1 text-[12px] leading-5 text-neutral-500">
          Start with a policy, then expand to catalogs, matching assets, and protected columns. Counts come from tenant policies and assets.
        </p>
      </div>
    );
  }

  const parsed = nodeKindFromId(selectedId);
  if (parsed.kind === 'policy') {
    const policy = policyById(catalog, parsed.rest);
    if (!policy) return <EmptyDetails />;
    const coverage = policyCoverage(catalog, policy);
    const models = inheritedModels(catalog, policy);
    const matched = featuredThenRest(matchedAssets(catalog, policy));
    const policyOverlaps = overlapsForPolicy(overlaps, policy.id);
    return (
      <div className="space-y-3 text-[12px]">
        <Header title={policy.name} subtitle={`${POLICY_TYPE_LABEL[policy.type]} · ${policy.status === 'ACTIVE' ? 'Active' : 'Draft'}`} />
        <Field
          label="Ownership"
          value={`${OWNERSHIP_LABEL[policy.ownership.level]} · ${ownerName(catalog, policy)}`}
        />
        <p className="rounded border border-neutral-200 bg-neutral-50 px-2 py-1.5 text-[12px] text-neutral-700">
          {policy.ownership.level === 'VAULT' && 'Created on the DataVault and inherited by its models and assets. Stored once.'}
          {policy.ownership.level === 'MODEL' && 'Created on the data model. Inherited by assets in this model’s access context.'}
          {policy.ownership.level === 'ASSET' && 'Created on the asset. Applies locally and is not inherited further.'}
        </p>
        <Field label="Description" value={policy.description} />
        <Field label="Targeting" value={policy.targeting} />
        <Field label="Audience" value={policy.audience.label} />
        <Field label="Action" value={policy.action.label} />
        {policy.mandatory ? <Field label="Inheritance" value="Mandatory parent restriction. Child policies cannot weaken it." /> : <Field label="Inheritance" value="Inherited by applicable models and assets in this owner’s access context. Stored once." />}
        <div>
          <p className={LABEL}>Coverage</p>
          <p className="mt-1 text-neutral-800">{coverage.assetsEvaluated} assets evaluated · {coverage.assetsMatched} matched · {coverage.columnsProtected} columns protected</p>
          <p className="mt-1 text-neutral-500">{coverage.scopedAssetIds.length - coverage.matchedAssetIds.length} assets are in scope and do not match.</p>
        </div>
        {models.length ? (
          <div>
            <p className={LABEL}>Inherited by</p>
            <ul className="mt-1 space-y-1 text-neutral-800">
              {models.map((model) => <li key={model.id}>{model.name}</li>)}
            </ul>
          </div>
        ) : null}
        <div>
          <p className={LABEL}>Matching assets</p>
          <ul className="mt-1 space-y-1">
            {matched.slice(0, 8).map((asset) => {
              const protectedCount = matchingColumns(policy, asset).length;
              const open = openAssetId === asset.id;
              return (
                <li key={asset.id} className="rounded border border-neutral-200 px-2 py-1.5">
                  <button type="button" className="flex w-full items-center justify-between text-left" onClick={() => onOpenAsset(open ? null : asset.id)}>
                    <span className="font-medium text-neutral-900">{asset.name}</span>
                    <span className="text-neutral-500">{protectedCount} protected columns</span>
                  </button>
                  {open ? (
                    <div className="mt-1">
                      <p className="text-neutral-500">{matchingColumns(policy, asset).map((column) => column.name).join(', ') || 'Row or asset restriction; no column list.'}</p>
                      <button type="button" className="mt-1 text-[11px] text-neutral-700 underline" onClick={() => onExpandAsset(asset.id)}>Show on graph</button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {matched.length > 8 ? <p className="mt-1 text-neutral-500">And {matched.length - 8} more matching assets. Expand on the graph to inspect a subset.</p> : null}
        </div>
        {policy.dependsOnAssetId ? <Field label="Depends on" value={assetById(catalog, policy.dependsOnAssetId)?.name ?? policy.dependsOnAssetId} /> : null}
        {policyOverlaps.length ? (
          <div>
            <p className={LABEL}>Overlaps</p>
            <ul className="mt-1 space-y-2">
              {policyOverlaps.slice(0, 6).map((item) => {
                const otherId = item.policyIds.find((id) => id !== policy.id);
                const other = otherId ? policyById(catalog, otherId) : undefined;
                return (
                  <li key={item.id} className="rounded border border-neutral-200 px-2 py-1.5">
                    <p className="font-medium text-neutral-900">{item.resource.label}</p>
                    <p className="text-neutral-500">{other?.name} · {OWNERSHIP_LABEL[other?.ownership.level ?? 'VAULT']} · {other?.action.label}</p>
                    <p className="mt-1 text-neutral-700">{item.kind === 'compatible' ? 'Compatible' : item.kind === 'conflict' ? 'Conflict' : 'Unresolved'} · {item.effectiveLabel}</p>
                    <p className="mt-1 text-neutral-500">{item.explanation}</p>
                    {item.attention ? <p className="mt-1 text-neutral-800">Administrator attention is required.</p> : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        <p className="text-[11px] text-neutral-400">Simulation only.</p>
      </div>
    );
  }

  if (parsed.kind === 'asset') {
    const asset = assetById(catalog, parsed.rest);
    if (!asset) return <EmptyDetails />;
    const hits = hitsForAsset(catalog, asset, user);
    const inherited = hits.filter((hit) => hit.policy.ownership.assetId !== asset.id);
    const local = hits.filter((hit) => hit.policy.ownership.assetId === asset.id);
    const effective = effectiveForAsset(catalog, asset, user);
    const models = asset.modelIds.map((id) => modelById(catalog, id)?.name).filter(Boolean);
    const vault = vaultById(catalog, asset.vaultId);
    const protectedCols = asset.columns.filter((column) => hits.some((hit) => matchingColumns(hit.policy, asset).some((item) => item.name === column.name)));
    return (
      <div className="space-y-3 text-[12px]">
        <Header title={asset.name} subtitle={asset.source} />
        <Field label="DataVault" value={vault?.name ?? asset.vaultId} />
        <Field label="Data Models" value={models.join(', ') || '—'} />
        <div>
          <p className={LABEL}>Applicable policies</p>
          <ul className="mt-1 space-y-1">
            {hits.map((hit) => (
              <li key={hit.policy.id}>
                <button type="button" className="text-left text-neutral-800 underline" onClick={() => onSelect(`policy:${hit.policy.id}`)}>
                  {hit.policy.name}
                </button>
                <span className="text-neutral-500"> · {OWNERSHIP_LABEL[hit.origin]} · {hit.actionLabel}{hit.policy.ownership.assetId === asset.id ? ' · Local' : ' · Inherited'}</span>
              </li>
            ))}
          </ul>
        </div>
        <Field label="Inherited policies" value={inherited.map((hit) => hit.policy.name).join(', ') || 'None'} />
        <Field label="Local policies" value={local.map((hit) => hit.policy.name).join(', ') || 'None'} />
        <Field label="Protected columns" value={protectedCols.map((column) => column.name).join(', ') || 'None'} />
        <EffectiveBlock effective={effective} />
      </div>
    );
  }

  if (parsed.kind === 'column') {
    const [assetId, columnName] = [parsed.rest.split(':')[0], parsed.rest.split(':').slice(1).join(':')];
    const asset = assetById(catalog, assetId);
    const column = asset?.columns.find((item) => item.name === columnName);
    if (!asset || !column) return <EmptyDetails />;
    const effective = effectiveForColumn(catalog, asset, column, user);
    const related = overlapsForResource(overlaps, asset.id, column.name);
    return (
      <div className="space-y-3 text-[12px]">
        <Header title={`${asset.name}.${column.name}`} subtitle={`${column.type} · ${column.classification}`} />
        <div>
          <p className={LABEL}>Matching policies</p>
          <ol className="mt-1 list-decimal space-y-1 pl-4">
            {effective.hits.map((hit) => (
              <li key={hit.policy.id} className="text-neutral-800">
                {hit.policy.name} — {OWNERSHIP_LABEL[hit.origin]} — {hit.actionLabel}
                {hit.appliesToUser ? '' : ' (does not apply to this user context)'}
              </li>
            ))}
          </ol>
        </div>
        <EffectiveBlock effective={effective} />
        {related.length ? (
          <div>
            <p className={LABEL}>Overlaps on this column</p>
            <ul className="mt-1 space-y-1 text-neutral-600">
              {related.map((item) => (
                <li key={item.id}>{item.explanation}{item.attention ? ' Administrator attention is required.' : ''}</li>
              ))}
            </ul>
          </div>
        ) : null}
        <p className="text-[11px] text-neutral-400">Effective protection is a frontend simulation for the {user.label} context.</p>
      </div>
    );
  }

  if (parsed.kind === 'model') {
    const model = modelById(catalog, parsed.rest);
    if (!model) return <EmptyDetails />;
    const policy = selectedPolicyId ? policyById(catalog, selectedPolicyId) : undefined;
    const assets = featuredThenRest(
      policy
        ? matchedAssets(catalog, policy).filter((asset) => asset.modelIds.includes(model.id))
        : catalog.assets.filter((asset) => asset.modelIds.includes(model.id)),
    );
    return (
      <div className="space-y-3 text-[12px]">
        <Header title={model.name} subtitle="Data Model" />
        <Field label="DataVault" value={vaultById(catalog, model.vaultId)?.name ?? model.vaultId} />
        <Field label="Assets in access context" value={String(model.assetIds.length)} />
        {policy ? <Field label="Matching the selected policy" value={`${assets.length} of ${scopedAssets(catalog, policy).filter((asset) => asset.modelIds.includes(model.id)).length} assets`} /> : null}
        <ul className="space-y-1">
          {assets.slice(0, 8).map((asset) => (
            <li key={asset.id}>
              <button type="button" className="text-neutral-800 underline" onClick={() => onExpandAsset(asset.id)}>{asset.name}</button>
            </li>
          ))}
        </ul>
        {assets.length > 8 ? <p className="text-neutral-500">And {assets.length - 8} more. The graph shows a subset.</p> : null}
      </div>
    );
  }

  if (parsed.kind === 'vault') {
    const vault = vaultById(catalog, parsed.rest);
    if (!vault) return <EmptyDetails />;
    const owned = catalog.policies.filter((policy) => policy.ownership.vaultId === vault.id && policy.ownership.level === 'VAULT');
    return (
      <div className="space-y-3 text-[12px]">
        <Header title={vault.name} subtitle="DataVault" />
        <Field label="Description" value={vault.description} />
        <div>
          <p className={LABEL}>Owned policies</p>
          <ul className="mt-1 space-y-1">
            {owned.map((policy) => {
              const coverage = policyCoverage(catalog, policy);
              return (
                <li key={policy.id}>
                  <button type="button" className="font-medium text-neutral-900 underline" onClick={() => onSelect(`policy:${policy.id}`)}>{policy.name}</button>
                  <p className="text-neutral-500">{OWNERSHIP_LABEL[policy.ownership.level]} · {POLICY_TYPE_LABEL[policy.type]} · {coverage.assetsEvaluated} evaluated · {coverage.assetsMatched} matched · {coverage.columnsProtected} columns</p>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    );
  }

  return <EmptyDetails />;
}

function Header({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div>
      <p className="text-[13px] font-semibold text-neutral-900">{title}</p>
      <p className="text-[12px] text-neutral-500">{subtitle}</p>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className={LABEL}>{label}</p>
      <p className="mt-1 text-neutral-800">{value}</p>
    </div>
  );
}

function EffectiveBlock({ effective }: { effective: ReturnType<typeof effectiveForColumn> }) {
  return (
    <div className="rounded border border-neutral-300 bg-neutral-50 px-2.5 py-2">
      <p className={LABEL}>Effective result</p>
      <p className="mt-1 font-medium text-neutral-900">{effective.label}</p>
      <p className="mt-1 leading-5 text-neutral-600">{effective.explanation}</p>
      {effective.filters.length ? <p className="mt-1 text-neutral-500">Filters: {effective.filters.join(' AND ')}</p> : null}
      {effective.attention ? <p className="mt-1 text-neutral-800">Administrator attention is required.</p> : null}
      <p className="mt-1 text-[11px] text-neutral-400">Frontend simulation.</p>
    </div>
  );
}

function EmptyDetails() {
  return <p className="text-[12px] text-neutral-500">Nothing is selected.</p>;
}
